import React from "react";

import { PANEL_MODES, stepKind } from "./stepKinds";
import { buildChapters, hasChapters, locateStep } from "../territory_intro/chapters";
import { getChartSpec } from "../terrain3d/chartSpecs";
import { LAYER_ORDER } from "../terrain3d/layerSpecs";

/**
 * STORYBOARD — la séquence du module, en bas de l'éditeur.
 *
 * DEUX ÉTATS, comme le sélecteur de pages d'un outil de présentation.
 *
 * Replié (par défaut) : une barre fine qui dit UNIQUEMENT où l'on est —
 * « Étape 4 sur 7 » — plus une piste segmentée, un segment par étape. Elle
 * occupe une quarantaine de pixels au lieu de cent-vingt, rendus à la carte,
 * et elle reste cliquable : chaque segment est une étape.
 *
 * Déplié : les vignettes. Elles portent maintenant un titre de section et des
 * connecteurs entre elles, parce que la première version ne se lisait pas
 * comme une séquence — on ne comprenait pas que ces cartes étaient les AUTRES
 * étapes du même module.
 *
 * IL EST DÉSORMAIS LA LIGNE DU BAS D'UN BANDEAU À DEUX ÉTAGES (cf.
 * ComposerStoryboard) : au-dessus de lui, le déroulé de l'atelier auquel la
 * page appartient. Il n'est donc plus maître de son repli — une seule des deux
 * lignes peut être ouverte, et cette exclusion ne peut se décider que d'un cran
 * plus haut.
 */

// Exporté : la pile d'étapes du composeur affichait le même résumé, réécrit
// en cascade de ternaires — deux copies à mettre à jour à chaque nouveau type,
// dont une qu'on oubliait.
export function stepSummary(step) {
  switch (step.kind) {
    case "message": return (step.message?.text || "").slice(0, 70);
    case "slide": return step.slide?.image_url ? "Image déposée" : "Aucune image";
    case "quiz": return step.quiz?.question || `${(step.quiz?.cards || []).length} carte(s)`;
    case "quiz_mouvant": return step.quiz_mouvant?.question || `${(step.quiz_mouvant?.options || []).length} proposition(s)`;
    // Nommer les graphiques plutôt que les compter : dans une vignette, « Débit
    // mensuel d'une station » situe l'étape, « 1 graphique » ne dit rien.
    case "graph": {
      if (step.charts) {
        const titles = step.charts
          .map((chart) => chart.title || getChartSpec(chart.chart_id)?.label)
          .filter(Boolean);
        return titles.length ? titles.join(" · ") : "Aucun graphique";
      }
      return `${(step.list_graph || []).length} composant(s)`;
    }
    case "points_of_interest": return `${(step.points_of_interest?.points || []).length} point(s)`;
    default: return "Carte libre";
  }
}

export default function StepStoryboard({
  steps, activeIndex, expanded, onToggle, onSelect, onAdd, onRemove,
}) {
  const goPrevious = () => activeIndex > 0 && onSelect(activeIndex - 1);
  const goNext = () => activeIndex < steps.length - 1 && onSelect(activeIndex + 1);

  // Le storyboard montre le découpage que la séance montrera : même règle,
  // même module (cf. territory_intro/chapters.js). L'auteur voit sa structure
  // là où il l'écrit, sans avoir à lancer un aperçu pour la vérifier.
  const chapters = buildChapters(steps);
  const chaptered = hasChapters(chapters);
  const located = locateStep(chapters, activeIndex);

  // Une vignette, dessinée une fois : elle est rendue dans une bande à plat
  // quand le module n'a pas de chapitres, et dans un groupe par chapitre
  // sinon.
  const stepCard = (step, idx) => {
    const kind = stepKind(step.kind);
    const layerCount = (step.active_layers || []).filter((id) => LAYER_ORDER.includes(id)).length;
    return (
      <button
        type="button"
        className={`step-storyboard-card${idx === activeIndex ? " is-active" : ""}`}
        onClick={() => onSelect(idx)}
      >
        <span className="step-storyboard-index">{idx + 1}</span>
        <span className="step-storyboard-sketch">{kind?.sketch}</span>
        <span className="step-storyboard-body">
          <span className="step-storyboard-kind">{kind?.label}</span>
          <span className="step-storyboard-summary">{stepSummary(step)}</span>
          <span className="step-storyboard-badges">
            {/* Le mode de panneau distingue deux étapes du même
                type, faute de miniature du rendu. */}
            <span className="step-storyboard-badge" title="Position du contenu">
              {PANEL_MODES.find((m) => m.id === (step.panel_mode || "normal"))?.label}
            </span>
            <span className="step-storyboard-badge" title="Couches allumées">
              {layerCount} couche{layerCount > 1 ? "s" : ""}
            </span>
            {!step.camera && (
              <span className="step-storyboard-badge is-warn" title="Aucun cadrage capturé">
                ⚠ cadrage
              </span>
            )}
          </span>
        </span>
        <span
          className="step-storyboard-remove"
          role="button"
          tabIndex={-1}
          title="Supprimer cette étape"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(idx);
          }}
        >
          ✕
        </span>
      </button>
    );
  };

  return (
    <div className="storyboard-row steps-storyboard">
      <div className="step-storyboard-bar">
        <button
          type="button"
          className="step-storyboard-toggle"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <span className={`step-storyboard-chevron${expanded ? " is-open" : ""}`} aria-hidden="true" />
          {expanded ? "Masquer les étapes" : "Voir les étapes"}
        </button>

        <div className="step-storyboard-progress">
          <button
            type="button"
            className="step-storyboard-arrow"
            onClick={goPrevious}
            disabled={activeIndex <= 0}
            aria-label="Étape précédente"
          >
            ‹
          </button>

          {/* La position, dite comme la séance la dira : le sujet d'abord, le
              rang dans le sujet ensuite. Le rang absolu reste en second, parce
              qu'ici — contrairement à la séance — il sert encore à se repérer
              dans une liste qu'on édite. */}
          <span className="step-storyboard-position">
            {chaptered && located ? (
              <>
                <strong>{located.chapter.title || "Sans chapitre"}</strong>
                {" "}· {located.stepInChapter}/{located.chapter.count}
                <span className="step-storyboard-position-abs"> (étape {activeIndex + 1}/{steps.length})</span>
              </>
            ) : (
              <>Étape <strong>{activeIndex + 1}</strong> sur {steps.length}</>
            )}
          </span>

          {/* Piste segmentée : l'avancement ET un accès direct. Une barre de
              progression pleine dirait où l'on en est sans permettre d'y
              aller ; un segment par étape fait les deux. Les segments sont
              groupés par chapitre, comme dans le bandeau de la séance : la
              même piste, aux mêmes coupures. */}
          <div className="step-storyboard-track" role="tablist" aria-label="Étapes du module">
            {chapters.map((chapter, chapterIndex) => (
              <span
                key={`${chapter.start}-${chapter.title || ""}`}
                className={
                  "step-storyboard-trackgroup" +
                  (located && chapterIndex === located.chapterIndex ? " is-current" : "")
                }
                style={{ flexGrow: chapter.count }}
                title={chapter.title || "Sans chapitre"}
              >
                {steps.slice(chapter.start, chapter.start + chapter.count).map((step, offset) => {
                  const idx = chapter.start + offset;
                  return (
                    <button
                      key={step.step_id || idx}
                      type="button"
                      role="tab"
                      aria-selected={idx === activeIndex}
                      aria-label={`Étape ${idx + 1} : ${stepKind(step.kind)?.label || step.kind}`}
                      title={`${idx + 1}. ${stepKind(step.kind)?.label || step.kind}${step.camera ? "" : " (cadrage non capturé)"}`}
                      // Piste unie : seule l'étape courante se distingue. Les
                      // états (passée, sans cadrage) restent dits par l'infobulle
                      // et par les vignettes, là où on peut les traiter.
                      className={`step-storyboard-seg${idx === activeIndex ? " is-active" : ""}`}
                      onClick={() => onSelect(idx)}
                    />
                  );
                })}
              </span>
            ))}
          </div>

          <button
            type="button"
            className="step-storyboard-arrow"
            onClick={goNext}
            disabled={activeIndex >= steps.length - 1}
            aria-label="Étape suivante"
          >
            ›
          </button>
        </div>

        <button type="button" className="step-storyboard-addbtn" onClick={onAdd}>
          + Étape
        </button>
      </div>

      {expanded && (
        /* SANS CHAPITRES, une bande à plat — c'est ce qu'elle a toujours été.
           AVEC, la bande devient une suite de groupes coiffés du nom du sujet :
           la structure du module se lit alors sans ouvrir une étape, et une
           étape tombée dans le mauvais groupe se voit. */
        <div className={`step-storyboard-strip${chaptered ? " is-chaptered" : ""}`}>
          {chaptered ? (
            chapters.map((chapter, chapterIndex) => (
              <div
                key={`${chapter.start}-${chapter.title || ""}`}
                className={
                  "step-storyboard-chapter" +
                  (located && chapterIndex === located.chapterIndex ? " is-current" : "") +
                  (chapter.title ? "" : " is-untitled")
                }
              >
                <span className="step-storyboard-chapter-head">
                  <span className="step-storyboard-chapter-name">
                    {chapter.title || "Sans chapitre"}
                  </span>
                  <span className="step-storyboard-chapter-count">
                    {chapter.count} étape{chapter.count > 1 ? "s" : ""}
                  </span>
                </span>
                <div className="step-storyboard-chapter-cards">
                  {steps.slice(chapter.start, chapter.start + chapter.count).map((step, offset) => {
                    const idx = chapter.start + offset;
                    return (
                      <React.Fragment key={step.step_id || idx}>
                        {offset > 0 && <span className="step-storyboard-link" aria-hidden="true" />}
                        {stepCard(step, idx)}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            steps.map((step, idx) => (
              <React.Fragment key={step.step_id || idx}>
                {idx > 0 && <span className="step-storyboard-link" aria-hidden="true" />}
                {stepCard(step, idx)}
              </React.Fragment>
            ))
          )}
          <button type="button" className="step-storyboard-add" onClick={onAdd}>
            + Étape
          </button>
        </div>
      )}
    </div>
  );
}
